/**
 * Default siren factory used when no override is registered.
 *
 * Plays through the console's one shared AudioContext
 * (`audio-context.ts`). It used to make its own, lazily, when the
 * alarm triggered: "alarms only trigger after the user has interacted
 * with the page" was the assumption, and on an untouched iPad it was
 * false, so the context started suspended and the siren was silent.
 * Sharing it means the tap that unlocks the chime, on the "Tap to
 * enable alert sound" chip or anywhere, unlocks the siren too, and a
 * siren already going on a suspended context becomes audible on that
 * tap. Rises / falls between 660 Hz and 880 Hz at 0.5 Hz to create an
 * ambulance-style wail.
 */
import type { SirenHandle } from './fire-alarm.siren';
import { sharedAudioContext } from './audio-context';

let cachedCtx: AudioContext | null = null;
let cachedGain: GainNode | null = null;
let cachedOsc: OscillatorNode | null = null;
let cachedInterval: number | null = null;
let cachedPhase = false;

export function defaultSirenFactory(): SirenHandle | null {
  const ctx = sharedAudioContext();
  if (!ctx) return null;
  if (ctx !== cachedCtx) {
    cachedCtx = ctx;
    cachedGain = null;
    cachedOsc = null;
  }
  if (cachedCtx.state === 'suspended') {
    void cachedCtx.resume().catch(() => {});
  }
  if (!cachedGain || !cachedOsc) {
    cachedGain = cachedCtx.createGain();
    cachedGain.gain.value = 0.0;
    cachedGain.connect(cachedCtx.destination);
    cachedOsc = cachedCtx.createOscillator();
    cachedOsc.type = 'square';
    cachedOsc.frequency.value = 660;
    cachedOsc.connect(cachedGain);
    cachedOsc.start();
  }
  if (cachedInterval === null) {
    cachedPhase = false;
    cachedInterval = window.setInterval(() => {
      if (!cachedCtx || !cachedOsc || !cachedGain) return;
      cachedPhase = !cachedPhase;
      cachedOsc.frequency.value = cachedPhase ? 880 : 660;
      cachedGain.gain.value = cachedPhase ? 0.08 : 0.0;
    }, 500);
  }

  return {
    stop: () => {
      if (cachedInterval !== null) {
        window.clearInterval(cachedInterval);
        cachedInterval = null;
      }
      if (cachedGain) cachedGain.gain.value = 0;
    },
    isPlaying: () => cachedInterval !== null,
  };
}
